<template>
    <!-- Desktop Table (visible only on 2xl screens and above) -->
    <div class="hidden 2xl:block overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead class="bg-gray-50 dark:bg-black">
          <tr>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Customer
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Email
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Brand
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Category
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Vehicles
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Tires
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" class="px-3 sm:px-6 py-4 text-center text-xs font-bold text-slate-800 dark:text-gray-400 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="bg-white dark:bg-black divide-y divide-gray-200 dark:divide-gray-700">
          <tr
            v-for="customer in customers"
            :key="customer.user_id"
            class="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-600 odd:bg-white even:bg-gray-25 dark:odd:bg-gray-900 dark:even:bg-gray-800"
          >
            <!-- Customer Name -->
            <td class="px-3 sm:px-2 py-4 whitespace-nowrap text-sm max-w-xs lg:max-w-sm text-gray-900 dark:text-white text-center">
              <div class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {{ customer.first_name }} {{ customer.last_name }}
              </div>
            </td>

            <!-- Email -->
            <td class="px-3 sm:px-2 py-4 text-sm max-w-xs lg:max-w-sm text-center">
              <div class="truncate text-xs sm:text-sm text-gray-500 dark:text-gray-400" :title="customer.email">
                {{ customer.email }}
              </div>
            </td>

            <!-- Brand -->
            <td class="px-3 sm:px-2 py-4 whitespace-nowrap text-sm text-center">
              <span
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                :class="getBrandClass(customer.brand?.brand_name)"
              >
                {{ customer.brand?.brand_name || 'N/A' }}
              </span>
            </td>

            <!-- Category -->
            <td class="px-3 sm:px-2 py-4 whitespace-nowrap text-center">
              <span
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                :class="customer.category === 'internal'
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400'
                  : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'"
              >
                {{ customer.category === 'internal' ? 'Internal' : 'Customer' }}
              </span>
            </td>

            <!-- Vehicles -->
            <td class="px-3 sm:px-2 py-4 text-sm max-w-xs lg:max-w-sm text-center">
              <div class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {{ customer.vehicles_count || 0 }}
              </div>
            </td>

            <!-- Tires -->
            <td class="px-3 sm:px-2 py-4 text-sm max-w-xs lg:max-w-sm text-center">
              <div class="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {{ customer.tires_count || 0 }}
              </div>
            </td>
            
            <!-- Status -->
            <td class="px-3 sm:px-2 py-4 whitespace-nowrap text-center">
              <span
                class="inline-flex items-center px-2 sm:px-2.5 py-1 rounded-full text-xs font-medium"
                :class="getStatusClass(customer.status)"
              >
                <span
                  class="w-1.5 h-1.5 rounded-full mr-1 sm:mr-1.5"
                  :class="getStatusDotClass(customer.status)"
                ></span>
                {{ customer.status }}
              </span>
            </td>


            <!-- Actions -->
            <td class="px-3 sm:px-2 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
              <div class="flex items-center justify-center space-x-1 lg:space-x-2">
                <div class="relative group inline-block">
                  <button
                    @click.stop="$emit('view', customer)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ViewIcon classes="h-5 w-5" />
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    View Details
                  </div>
                </div>
                <div class="relative group inline-block">
                  <button
                    @click.stop="$emit('edit', customer)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <EditIcon classes="h-5 w-5" />
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    Edit Customer
                  </div>
                </div>
                <div class="relative group inline-block">
                  <button
                    @click.stop="$emit('reset-password', customer)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <LockIcon classes="h-5 w-5" />
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    Reset Password
                  </div>
                </div>
                <div class="relative group inline-block">
                  <button
                    @click.stop="$emit('delete', customer)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <DeleteIcon classes="h-5 w-5" />
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    Delete Customer
                  </div>
                </div>
                <div class="relative group inline-block">
                  <button
                    @click.stop="$emit('update-category', customer)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    Update Category
                  </div>
                </div>
                <div class="relative group inline-block">
                  <button
                    @click.stop="$emit('remarks', customer)"
                    :disabled="loading"
                    class="inline-flex items-center p-1.5 border border-transparent rounded-md text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                  </button>
                  <div class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 w-max text-xs text-white bg-gray-800 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition pointer-events-none z-10">
                    Remarks
                  </div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile Cards (visible on 2xl screens and below) -->
    <div class="2xl:hidden divide-y divide-gray-200 dark:divide-gray-700">
      <div
        v-for="customer in customers"
        :key="customer.user_id"
        class="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
      >
        <!-- Header: Customer Name and Status -->
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center space-x-2">
            <span class="text-sm font-medium text-gray-900 dark:text-white">
              {{ customer.first_name }} {{ customer.last_name }}
            </span>
          </div>
          <span
            class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
            :class="getStatusClass(customer.status)"
          >
            <span
              class="w-1.5 h-1.5 rounded-full mr-1.5"
              :class="getStatusDotClass(customer.status)"
            ></span>
            {{ customer.status }}
          </span>
        </div>

        <!-- Email -->
        <div class="mb-3">
          <div class="text-sm font-medium text-gray-900 dark:text-white mb-1">
            Email:
          </div>
          <div class="text-sm text-gray-600 dark:text-gray-400 break-words">
            {{ customer.email }}
          </div>
        </div>

        <!-- Brand -->
        <div class="mb-3">
          <div class="text-sm font-medium text-gray-900 dark:text-white mb-1">
            Brand:
          </div>
          <div>
            <span
              class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
              :class="getBrandClass(customer.brand?.brand_name)"
            >
              {{ customer.brand?.brand_name || 'N/A' }}
            </span>
          </div>
        </div>

        <!-- Category -->
        <div class="mb-3">
          <div class="text-sm font-medium text-gray-900 dark:text-white mb-1">
            Category:
          </div>
          <div>
            <span
              class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
              :class="customer.category === 'internal'
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400'
                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'"
            >
              {{ customer.category === 'internal' ? 'Internal' : 'Customer' }}
            </span>
          </div>
        </div>

        <!-- Vehicles & Tires -->
        <div class="mb-3 grid grid-cols-2 gap-3">
          <div>
            <div class="text-sm font-medium text-gray-900 dark:text-white mb-1">
              Vehicles:
            </div>
            <div class="text-sm text-gray-600 dark:text-gray-400">
              {{ customer.vehicles_count || 0 }}
            </div>
          </div>
          <div>
            <div class="text-sm font-medium text-gray-900 dark:text-white mb-1">
              Tires:
            </div>
            <div class="text-sm text-gray-600 dark:text-gray-400">
              {{ customer.tires_count || 0 }}
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-wrap gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
          <button
            @click="$emit('view', customer)"
            :disabled="loading"
            class="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ViewIcon classes="h-4 w-4 mr-1" />
            View
          </button>
          <button
            @click="$emit('edit', customer)"
            :disabled="loading"
            class="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <EditIcon classes="h-4 w-4 mr-1" />
            Edit
          </button>
          <button
            @click="$emit('reset-password', customer)"
            :disabled="loading"
            class="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <LockIcon classes="h-4 w-4 mr-1" />
            Reset
          </button>
          <button
            @click="$emit('delete', customer)"
            :disabled="loading"
            class="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <DeleteIcon classes="h-4 w-4 mr-1" />
            Delete
          </button>
          <button
            @click="$emit('update-category', customer)"
            :disabled="loading"
            class="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            Category
          </button>
          <button
            @click="$emit('remarks', customer)"
            :disabled="loading"
            class="flex-1 inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
            Remarks
          </button>
        </div>
      </div>
    </div>
</template>

<script setup lang="ts">
import type { Customer } from '@/types/customer';
import ViewIcon from '@/components/icons/ViewIcon.vue';
import EditIcon from '@/components/icons/EditIcon.vue';
import DeleteIcon from '@/components/icons/DeleteIcon.vue';
import LockIcon from '@/components/icons/LockIcon.vue';

defineProps<{
  customers: Customer[];
  loading?: boolean;
}>();

defineEmits<{
  view: [customer: Customer];
  edit: [customer: Customer];
  'reset-password': [customer: Customer];
  delete: [customer: Customer];
  'update-category': [customer: Customer];
  remarks: [customer: Customer];
}>();

function getStatusClass(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-black/20 dark:text-gray-400';
  }
}

function getStatusDotClass(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-400';
    case 'pending':
      return 'bg-yellow-400';
    case 'rejected':
      return 'bg-red-400';
    default:
      return 'bg-gray-400';
  }
}

function getBrandClass(brandName: string | undefined): string {
  switch (brandName?.toLowerCase()) {
    case 'continental':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
    case 'falken':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-black/20 dark:text-gray-400';
  }
}
</script>

<style scoped>
/* Custom alternating row colors */
.bg-gray-25 {
  background-color: rgb(249, 250, 251);
}
</style>

